import React, { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { CompetitorComparison } from './CompetitorComparison';
import { CompetitorsQualities } from './CompetitorsQualities';
import { KeyCompetitors } from './KeyCompetitors';
import {
  companyQualitiesValues,
  COMPETITIVE_ANALYSIS_NAMES,
  competitorsComparisonValues,
  keyCompetitorsValues,
  prefix,
} from './utils/form';

import styles from './CompetitiveAnalysis.module.scss';

export const CompetitiveAnalysis: React.FC = memo(() => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKET_ANALYSIS,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKET_ANALYSIS.COMPETITIVE_ANALYSIS,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('marketAnalysisForm.title')}
          badgeText={t('marketAnalysisForm.competitors.title')}
          section={FOR_BANK_SECTIONS.MARKET_ANALYSIS}
        />
      </FormSectionCard>
      {/* 4.2.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.directCompetitors')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.2.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.attractingCustomers')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.ATTRACTING_COMPETITORS_CUSTOMERS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.ATTRACTING_COMPETITORS_CUSTOMERS)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.ATTRACTING_COMPETITORS_CUSTOMERS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.2.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.keyCompetitors.title')}
        collapsible
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS)}
        aiFieldType="complex"
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS)}
          initialItem={keyCompetitorsValues}
          buttonText={t('marketAnalysisForm.competitors.qualitiesButtonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <KeyCompetitors name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 4.2.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.qualities.title')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES)}
        aiFieldType="complex"
        collapsible
      >
        <CompetitorsQualities
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES)}
          disabled={!canAccess}
          title={t('marketAnalysisForm.competitors.yourCompanyName')}
          className={styles.competitorQualities}
        />
        <FieldArrayComponent
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES)}
          initialItem={companyQualitiesValues}
          buttonText={t('marketAnalysisForm.competitors.qualitiesButtonText')}
          disabled={!canAccess}
          maxLength={4}
          fieldArrayStyles={{ container: styles.rowContainer }}
        >
          {(namePrefix) => (
            <CompetitorsQualities
              name={namePrefix}
              disabled={!canAccess}
              title={t('marketAnalysisForm.competitors.qualitiesButtonText')}
            />
          )}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 4.2.5 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.difference')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_DIFFERENCE)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_DIFFERENCE)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_DIFFERENCE)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.2.6 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.purchasingProducts')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.PURCHASING_PRODUCTS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.PURCHASING_PRODUCTS)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.PURCHASING_PRODUCTS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.2.7 */}
      <FormSectionCard disabled={!canAccess} title={t('marketAnalysisForm.competitors.productsCompare')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.COMPARISON_ITEM)}
          buttonText={t('marketAnalysisForm.competitors.productsCompareButtonText')}
          initialItem={competitorsComparisonValues}
          disabled={!canAccess}
        >
          {(namePrefix) => <CompetitorComparison name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 4.2.8 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.futureExpectations')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.FUTURE_EXPECTATIONS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.FUTURE_EXPECTATIONS)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.FUTURE_EXPECTATIONS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.2.9 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.barriers')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.BARRIERS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.BARRIERS)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.BARRIERS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 4.2.10 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketAnalysisForm.competitors.changingOfCurrentMarket')}
        aiFieldName={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.CHANGING_OF_CURRENT_MARKET)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.CHANGING_OF_CURRENT_MARKET)}
          name={concatWithDot(prefix, COMPETITIVE_ANALYSIS_NAMES.CHANGING_OF_CURRENT_MARKET)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
});
